import {
  Table,
  Button,
  Drawer,
  Form,
  Input,
  Select,
  Space,
  message,
  Popconfirm,
  Tabs,
  Tag,
} from "antd";
import { PlusOutlined } from "@ant-design/icons";
import { useEffect, useState } from "react";
import AppLayout from "../../components/AppLayout";
import {
  getUsers,
  createStaff,
  toggleApproval,
  updateUser,
  deleteUser,
} from "../../services/userService";

export default function Users() {
  const [users, setUsers] = useState([]);
  const [open, setOpen] = useState(false);
  const [editUser, setEditUser] = useState(null);
  const [activeTab, setActiveTab] = useState("customer");
  const [search, setSearch] = useState("");
  const [form] = Form.useForm();

  useEffect(() => {
    const t = setTimeout(() => {}, 300);
    return () => clearTimeout(t);
  }, [search]);
  /* Load users */
  useEffect(() => {
    loadUsers();
  }, []);

  const loadUsers = async () => {
    try {
      const res = await getUsers();
      setUsers(res.data);
    } catch {
      message.error("Failed to load users");
    }
  };

  /* Open drawer for new user */
  const openCreate = () => {
    setEditUser(null);
    form.resetFields();
    setOpen(true);
  };

  /* Open drawer for edit */
  const openEdit = (user) => {
    setEditUser(user);

    form.setFieldsValue({
      name: user.name,
      email: user.email,
      shop_name: user.shop_name,
      shop_code: user.shop_code,
      contact_no: user.contact_no,
      role: user.role,
      approved: user.approved,
      password: "",
    });

    setOpen(true);
  };

  const filteredUsers = users
    .filter((u) => {
      if (activeTab === "customer") return u.role === "customer";
      return ["manager", "employee"].includes(u.role);
    })
    .filter((u) =>
      `${u.name} ${u.email}`.toLowerCase().includes(search.toLowerCase()),
    );

  const customerCount = users.filter((u) => u.role === "customer").length;
  const internalCount = users.filter((u) =>
    ["manager", "employee"].includes(u.role),
  ).length;
  /* Create / Update submit */
  const submit = async (values) => {
    try {
      if (editUser) {
        await updateUser(editUser.id, values);
        message.success("User updated");
      } else {
        await createStaff(values);
        message.success("User created");
      }

      setOpen(false);
      form.resetFields();
      setEditUser(null);
      loadUsers();
    } catch (err) {
      message.error(err.response?.data?.message || "Operation failed");
    }
  };

  /* Toggle approval */
  const toggle = async (id, currentStatus) => {
    try {
      await toggleApproval(id);

      message.success(
        currentStatus
          ? "User blocked successfully"
          : "User approved successfully",
      );

      loadUsers();
    } catch {
      message.error("Status update failed");
    }
  };

  /* Delete user */
  const remove = async (id) => {
    try {
      await deleteUser(id);
      message.success("User deleted");
      loadUsers();
    } catch {
      message.error("Delete failed");
    }
  };

  /* Table columns */
  const columns = [
    {
      title: "Name",
      dataIndex: "name",
    },
    {
      title: "Email",
      dataIndex: "email",
    },
    // Customer-specific columns
    ...(activeTab === "internal"
      ? [
          {
            title: "Role",
            dataIndex: "role",
            render: (value) => value || "-",
          },
        ]
      : []),

    // Customer-specific columns
    ...(activeTab === "customer"
      ? [
          {
            title: "Shop Name",
            dataIndex: "shop_name",
            render: (value) => value || "-",
          },
          {
            title: "Shop Code",
            dataIndex: "shop_code",
            render: (value) => value || "-",
          },
        ]
      : []),

    {
      title: "Status",
      render: (_, r) =>
        r.approved ? (
          <Tag color="green">Approved</Tag>
        ) : (
          <Tag color="orange">Pending</Tag>
        ),
    },

    {
      title: "Actions",
      render: (_, r) => (
        <Space>
          {/* Approve / Block only customers */}
          {r.role === "customer" && (
            <Popconfirm
              title={
                r.approved
                  ? "Are you sure you want to block this user?"
                  : "Approve this user?"
              }
              onConfirm={() => toggle(r.id, r.approved)}
              okText="Yes"
              cancelText="No"
            >
              <Button
                size="small"
                type={r.approved ? "default" : "primary"}
                danger={r.approved}
              >
                {r.approved ? "Block" : "Approve"}
              </Button>
            </Popconfirm>
          )}

          <Button size="small" onClick={() => openEdit(r)}>
            Edit
          </Button>

          <Popconfirm
            title="Delete this user?"
            onConfirm={() => remove(r.id)}
            okText="Yes"
            cancelText="No"
          >
            <Button size="small" danger>
              Delete
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <AppLayout>
      <Space style={{ marginTop: 16, marginBottom: 10 }}>
        <Input
          placeholder="Search users..."
          style={{ width: 250 }}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        {/* Create button */}
        <Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>
          Create User
        </Button>
      </Space>

      <Tabs
        activeKey={activeTab}
        onChange={setActiveTab}
        items={[
          {
            key: "customer",
            label: `Customers (${customerCount})`,
          },
          {
            key: "internal",
            label: `Internal (${internalCount})`,
          },
        ]}
      />
      {/* Users table */}
      <Table
        style={{ marginTop: 10 }}
        columns={columns}
        dataSource={filteredUsers}
        rowKey="id"
        bordered
      />

      {/* Drawer */}
      <Drawer
        title={editUser ? "Edit User" : "Create User"}
        open={open}
        onClose={() => {
          setOpen(false);
          setEditUser(null);
        }}
        width={420}
        destroyOnClose
      >
        <Form layout="vertical" form={form} onFinish={submit}>
          <Form.Item
            name="name"
            label="Name"
            rules={[
              {
                required: true,
                message: "Please enter name",
              },
            ]}
          >
            <Input />
          </Form.Item>

          <Form.Item
            name="email"
            label="Email"
            rules={[
              {
                required: true,
                message: "Please enter email",
              },
              {
                type: "email",
                message: "Enter a valid email",
              },
            ]}
          >
            <Input />
          </Form.Item>

          <Form.Item
            name="contact_no"
            label="Mobile Number"
            rules={[
              {
                pattern: /^\+?[0-9]{7,15}$/,
                message: "Enter a valid mobile number",
              },
            ]}
          >
            <Input placeholder="+447911123456" maxLength={16} />
          </Form.Item>

          <Form.Item name="shop_name" label="Shop Name">
            <Input />
          </Form.Item>

          <Form.Item name="shop_code" label="Shop Code">
            <Input />
          </Form.Item>

          <Form.Item
            name="role"
            label="Role"
            rules={[
              {
                required: true,
                message: "Please select role",
              },
            ]}
          >
            <Select
              options={[
                {
                  value: "admin",
                  label: "Admin",
                },
                {
                  value: "manager",
                  label: "Manager",
                },
                {
                  value: "employee",
                  label: "Employee",
                },
                {
                  value: "customer",
                  label: "Customer",
                },
              ]}
            />
          </Form.Item>

          <Form.Item
            name="approved"
            label="Status"
            rules={[
              {
                required: true,
              },
            ]}
          >
            <Select
              options={[
                {
                  value: true,
                  label: "Approved",
                },
                {
                  value: false,
                  label: "Blocked",
                },
              ]}
            />
          </Form.Item>

          {editUser && (
            <Form.Item
              name="password"
              label="New Password"
              extra="Leave blank to keep the current password."
            >
              <Input.Password />
            </Form.Item>
          )}

          <Button type="primary" htmlType="submit" block>
            {editUser ? "Update User" : "Create User"}
          </Button>
        </Form>
      </Drawer>
    </AppLayout>
  );
}
